import * as React from 'react';
import { FileText, CheckCircle, Building2 } from 'lucide-react';
import { Card, Text, Metric, Flex, ProgressBar } from '@tremor/react';
import type { LeaseholderStats, CommunityStats } from '../../types/lease';

interface StatsGridProps {
  loading: boolean;
  leaseholderStats: LeaseholderStats[];
  communityStats: CommunityStats[];
}

export default function StatsGrid({ loading, leaseholderStats, communityStats }: StatsGridProps) {
  // Calculate aggregate stats
  const stats = React.useMemo(() => {
    const totalActiveLeases = communityStats.reduce((sum, stat) => sum + (stat.active_leases || 0), 0);
    const totalAvailableLots = communityStats.reduce((sum, stat) => sum + (stat.available_lots || 0), 0);
    const totalLots = totalActiveLeases + totalAvailableLots;
    const occupancyRate = totalLots > 0 ? (totalActiveLeases / totalLots) * 100 : 0;

    return {
      activeLeases: totalActiveLeases,
      availableLots: totalAvailableLots,
      occupancyRate: occupancyRate.toFixed(1)
    };
  }, [leaseholderStats, communityStats]);

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {[...Array(3)].map((_, i) => (
          <Card key={i} className="animate-pulse">
            <div className="h-4 bg-gray-200 rounded w-1/2 mb-4"></div>
            <div className="h-8 bg-gray-200 rounded w-3/4"></div>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <Card decoration="top" decorationColor="green">
        <Flex alignItems="start">
          <div>
            <Text>Active Leases</Text>
            <Metric>{stats.activeLeases}</Metric>
            <Text className="mt-2 text-sm">Currently active</Text>
          </div>
          <CheckCircle className="text-green-500" />
        </Flex>
      </Card>

      <Card decoration="top" decorationColor="amber">
        <Flex alignItems="start">
          <div>
            <Text>Available Lots</Text>
            <Metric>{stats.availableLots}</Metric>
            <Text className="mt-2 text-sm">Ready for lease</Text>
          </div>
          <FileText className="text-amber-500" />
        </Flex>
      </Card>

      <Card decoration="top" decorationColor="purple">
        <div>
          <Flex alignItems="start">
            <div>
              <Text>Occupancy Rate</Text>
              <Metric>{stats.occupancyRate}%</Metric>
            </div>
            <Building2 className="text-purple-500" />
          </Flex>
          <ProgressBar value={parseFloat(stats.occupancyRate)} color="purple" className="mt-3" />
          <Text className="mt-2 text-sm">Of total lots</Text>
        </div>
      </Card>

    </div>
  );
}
